import { useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Users, Clock, Timer, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

function formatHours(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);
  if (hours === 0 && mins === 0) return '0h';
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}

function isToday(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function StatsBar({ onSiteCount, checkIns = [], gapAnalysis = [], gapAnalysisCounts = {} }) {
  const { metCount = 0 } = gapAnalysisCounts;

  const { totalHoursToday, avgShiftDuration, complianceScore } = useMemo(() => {
    const todayCheckIns = checkIns.filter(
      (c) => c.check_in_time && isToday(c.check_in_time)
    );

    const now = new Date();
    let totalMinutes = 0;
    let completedMinutes = 0;
    let completedCount = 0;

    todayCheckIns.forEach((c) => {
      if (c.check_out_time) {
        const durationMs = new Date(c.check_out_time) - new Date(c.check_in_time);
        const mins = durationMs / 60000;
        totalMinutes += mins;
        completedMinutes += mins;
        completedCount += 1;
      } else {
        const durationMs = now - new Date(c.check_in_time);
        const mins = durationMs / 60000;
        totalMinutes += mins;
      }
    });

    const avgMins = completedCount > 0 ? completedMinutes / completedCount : 0;
    const score =
      gapAnalysis.length > 0
        ? Math.round((metCount / gapAnalysis.length) * 100)
        : 100;

    return {
      totalHoursToday: totalMinutes,
      avgShiftDuration: avgMins,
      complianceScore: score,
    };
  }, [checkIns, gapAnalysis, metCount]);

  const complianceColor =
    complianceScore >= 80
      ? { bg: 'bg-emerald-100', text: 'text-emerald-600' }
      : complianceScore >= 50
        ? { bg: 'bg-amber-100', text: 'text-amber-600' }
        : { bg: 'bg-red-100', text: 'text-red-600' };

  const stats = [
    {
      label: 'On-Site Now',
      value: onSiteCount,
      icon: Users,
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
    },
    {
      label: 'Hours Today',
      value: formatHours(totalHoursToday),
      icon: Clock,
      bg: 'bg-blue-100',
      text: 'text-blue-600',
    },
    {
      label: 'Avg Shift',
      value: formatHours(avgShiftDuration),
      icon: Timer,
      bg: 'bg-violet-100',
      text: 'text-violet-600',
    },
    {
      label: 'Compliance',
      value: `${complianceScore}%`,
      icon: ShieldCheck,
      bg: complianceColor.bg,
      text: complianceColor.text,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-6 py-4">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.06 }}
            className="bg-white rounded-xl border border-border p-4 flex items-center gap-3"
          >
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                stat.bg
              )}
            >
              <Icon className={cn('h-5 w-5', stat.text)} />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground tracking-tight">
                {stat.value}
              </p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
